import { useLatestRef } from "@appretio/shared";
import type {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  MenuItemDef
} from "ag-grid-enterprise";
import { useCallback } from "react";
import {
  gridApiTarget,
  type GridWrite
} from "../../../hooks/useForecastWorkspace/useWorkspaceMutationQueue";
import type { WorkspaceTableState } from "../../../hooks/useForecastWorkspace/useWorkspaceTableState";
import { buildCopyMenuItems, type CopyMenuState } from "./copyMenuItems";
import type { CopyPlan, CopyRow } from "./copyPlan";
import type { CopyTarget } from "./copyTarget";
import { describeCopyResult } from "./describeCopyResult";

/** Options for {@link useCopyToOwnRows}. */
export interface UseCopyToOwnRowsOptions {
  /** The page state a copy is gated on. */
  state: CopyMenuState;
  /** The ticked rows, already enriched by `useMainPageState`. */
  selectedRows: readonly CopyRow[];
  /** Queues the copy through the grid's one serial writer; `false` if it is busy. */
  queueWrite: (write: GridWrite) => boolean;
  /** Raises the toast naming what is copied and what is not. */
  setToastState: WorkspaceTableState["setToastState"];
}

/**
 * The grid's `getContextMenuItems`, carrying the copy items above AG
 * Grid's own.
 *
 * The copy goes through the mutation queue rather than writing the grid
 * directly: it is one recompute whose cascade must not race an edit already
 * in flight (SPEC.md § Copy from the context menu).
 */
export function useCopyToOwnRows(options: UseCopyToOwnRowsOptions) {
  // AG Grid reads `gridOptions` once, when it creates the grid, and ignores
  // every object after it. The callback it keeps must read the page state as
  // it is now, or copy answers with what was true before the first Apply.
  const latest = useLatestRef(options);

  return useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const { state, selectedRows, queueWrite, setToastState } = latest.current;
      const defaults = params.defaultItems ?? [];

      // Neutral while in flight, success once landed, both kept until closed —
      // SPEC.md § The toast follows the write.
      function runCopy(plan: CopyPlan, target: CopyTarget) {
        const accepted = queueWrite({
          target: gridApiTarget(params.api),
          cellChanges: plan.changes,
          kind: "copy",
          onSettled: (landed) =>
            setToastState(
              landed
                ? {
                    open: true,
                    variant: "success",
                    duration: Infinity,
                    title: "Copy successful",
                    message: describeCopyResult(plan, "landed", target)
                  }
                : (prev) => ({ ...prev, open: false })
            )
        });
        // The menu greys the item on a render-lagged flag; a refusal here is
        // the gap between that read and the click. The planner clicked an
        // enabled item, so they are told nothing happened.
        if (!accepted) {
          setToastState({
            open: true,
            variant: "default",
            title: "Copy",
            message: "Grid update in progress. Nothing was copied."
          });
          return;
        }
        setToastState({
          open: true,
          variant: "default",
          duration: Infinity,
          title: "Copy",
          message: describeCopyResult(plan, "queued", target)
        });
      }

      const items = buildCopyMenuItems(
        state,
        params.api,
        params.node?.data as CopyRow | undefined,
        selectedRows,
        { onCopy: runCopy }
      );
      if (items.length === 0) return defaults;
      if (defaults.length === 0) return items;
      return [...items, "separator", ...defaults];
    },
    [latest]
  );
}
